import React, { useMemo, useRef } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  IconButton,
  Stack,
  Typography,
  useTheme,
  Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { motion } from "framer-motion";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const FollowUpHistoryView = ({
  entries = [],
  onSelect,
  onEdit,
  getTouchPointTypeLabel,
  normalizeFollowUpCategory,
  categoryColors = {},
  queryStatusColors = {},
}) => {
  const scrollerRef = useRef(null);
  const theme = useTheme()

  /*
   * Chronological sorting:
   * Oldest -> Newest
   * 9 AM -> 10 AM -> 11 AM
   */
  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const aTime = new Date(
        a?.dateTime || a?.startDate || 0
      ).getTime();

      const bTime = new Date(
        b?.dateTime || b?.startDate || 0
      ).getTime();

      return aTime - bTime;
    });
  }, [entries]);

  if (!sortedEntries.length) {
    return null;
  }

  const getEntryDate = (entry) => {
    return entry?.dateTime || entry?.startDate;
  };

  const formatDate = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getDirectionLabel = (value) => {
    if (value === "INCOMING") return "Incoming";
    if (value === "OUTGOING") return "Outgoing";
    return "";
  };

  const getCommunicationType = (entry) => {
    if (getTouchPointTypeLabel) {
      return getTouchPointTypeLabel(entry);
    }

    return (
      entry?.position ||
      entry?.communicationType ||
      "Touch-point"
    );
  };

  const getCategory = (entry) => {
    if (normalizeFollowUpCategory) {
      return normalizeFollowUpCategory(entry?.category);
    }

    return entry?.category || "Meeting Touch-point";
  };

  const getDescription = (entry) => {
    return (
      entry?.note ||
      entry?.description ||
      "No conversation notes available."
    );
  };

  /*
   * Scroll exactly one card.
   *
   * Card width = 380
   * Gap = 24
   * Total movement = 404
   */
  const scrollByCard = (direction) => {
    if (!scrollerRef.current) return;

    const cardWidth = 380;
    const gap = 24;

    scrollerRef.current.scrollBy({
      left:
        direction === "left"
          ? -(cardWidth + gap)
          : cardWidth + gap,
      behavior: "smooth",
    });
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        py: 2,
      }}
    >
      {/* TIMELINE LINE */}
      <Box
        sx={{
          position: "absolute",
          top: 43,
          left: 50,
          right: 50,
          height: 2,
          bgcolor: "primary.light",
          opacity: 0.5,
        }}
      />

      {/* LEFT CHEVRON */}
      <IconButton
        aria-label="Previous follow-up"
        onClick={() => scrollByCard("left")}
        size="small"
        sx={{
          position: "absolute",
          top: 27,
          left: 0,
          bgcolor: "#fff",
          border: "1px solid",
          borderColor: "grey.300",
          boxShadow: 1,
          zIndex: 5,

          "&:hover": {
            bgcolor: "grey.50",
          },
        }}
      >
        <ChevronLeftIcon />
      </IconButton>

      {/* RIGHT CHEVRON */}
      <IconButton
        aria-label="Next follow-up"
        onClick={() => scrollByCard("right")}
        size="small"
        sx={{
          position: "absolute",
          top: 27,
          right: 0,
          bgcolor: "#fff",
          border: "1px solid",
          borderColor: "grey.300",
          boxShadow: 1,
          zIndex: 5,

          "&:hover": {
            bgcolor: "grey.50",
          },
        }}
      >
        <ChevronRightIcon />
      </IconButton>

      {/* HORIZONTAL SCROLLER */}
      <Box
        ref={scrollerRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 3,
          overflowX: "auto",
          px: 6,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {sortedEntries.map((entry, index) => {
          const entryDate = getEntryDate(entry);

          const category = getCategory(entry);

          const status = entry?.queryStatus || "NA";

          const communicationType =
            getCommunicationType(entry);

          const directionLabel = getDirectionLabel(entry?.callDirection);

          const firstName =
            entry?.doneBy?.userFirstName || "";

          const lastName =
            entry?.doneBy?.userLastName || "";

          const fullName =
            `${firstName} ${lastName}`.trim() ||
            "Unknown User";

          return (
            <Box
              key={entry?._id || index}
              component={motion.div}
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: 0.4,
                delay: index * 0.05,
              }}
              sx={{
                minWidth: {
                  xs: 300,
                  sm: 350,
                  md: 380,
                },
                width: {
                  xs: 300,
                  sm: 350,
                  md: 380,
                },
                flexShrink: 0,
              }}
            >
              {/* DATE NODE */}
              <Box
                sx={{
                  height: 55,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: "#fff",
                    border: "1px solid",
                    borderColor: "primary.light",
                    borderRadius: 2,
                    boxShadow: 1,
                    minWidth: 130,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: "text.primary",
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    {formatDate(entryDate)}
                  </Typography>
                </Box>
              </Box>

              {/* FOLLOW-UP CARD */}
              <Card
                onClick={() => onSelect?.(entry)}
                sx={{
                  width: "100%",
                  height: 220,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "grey.200",
                  boxShadow:
                    "0 5px 18px rgba(15,23,42,0.06)",
                  overflow: "hidden",
                  cursor: onSelect
                    ? "pointer"
                    : "default",
                  transition:
                    "box-shadow 0.2s ease, transform 0.2s ease",

                  "&:hover": {
                    boxShadow:
                      "0 8px 24px rgba(15,23,42,0.10)",
                  },
                }}
              >
                {/* TOP SECTION */}
                <Box
                  sx={{
                    height: "10%",
                    minHeight: 70,
                    px: {
                      xs: 2,
                      sm: 2.5,
                    },
                    py:1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    borderBottom: "1px solid",
                    borderColor: "grey.200",
                    bgcolor: entry.callType === "GROUP" ? "#eafff9" : "#e0eaff",
                  }}
                >
                  {/* LEFT - USER */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    {/* <Avatar
                      src={entry?.doneBy?.userImageURL}
                      alt={fullName}
                      sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                      }}
                    /> */}

                    {/* <Box
                      sx={{
                        minWidth: 0,
                        
                      }}
                    > */}
                      {/* <Typography
                        sx={{
                          color: "text.primary",
                          fontSize: 13,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 140,
                        }}
                      >
                        {fullName}
                      </Typography> */}

                      <Stack
                        // direction="row"
                        sx={{display:"flex", flexDirection:"row", gap:"10px", alignItems:"center", minWidth:"100%"}}
                        // alignItems="center"
                        // spacing={0.5}
                      >
                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: 12,
                            fontWeight: 500,
                            textAlign:"center",
                            mb: "0 !important",
                          }}
                        >
                          {formatTime(entryDate)}
                        </Typography>

                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: 12,
                            fontWeight: 500,
                            textAlign:"center"
                          }}
                        >
                          •
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "text.primary",
                            mb: "0 !important",
                            textAlign:"center"
                          }}
                        >
                          {[directionLabel, communicationType].filter(Boolean).join(" ")}
                        </Typography>

                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: 12, 
                            fontWeight: 500,
                            textAlign:"center"
                          }}
                        >
                          •
                        </Typography>

                        {/* RIGHT - CHIPS */}
                        <Stack
                          direction="row"
                          spacing={0.5}
                          useFlexGap
                          flexWrap="wrap"
                          justifyContent="flex-end"
                          sx={{
                            flexShrink: 1,
                          }}
                        >
                          <Chip
                            size="small"
                            label={category}
                            sx={{
                              fontWeight: 500,
                              ...(
                                categoryColors[category] ||
                                categoryColors[
                                  "Meeting Touch-point"
                                ] ||
                                {}
                              ),
                            }}
                          />

                          <Chip
                            size="small"
                            label={status}
                            sx={{
                              fontWeight: 500,
                              ...(
                                queryStatusColors[status] ||
                                queryStatusColors.NA ||
                                {}
                              ),
                            }}
                          />

                          {onEdit && (
                            <Tooltip title="Edit Touch-point" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(entry);
                                }}
                                sx={{
                                  p: 0.5,
                                  ml: 0.25,
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: theme.palette.accent?.followUpLight || "primary.main",
                                    bgcolor: "rgba(0, 0, 0, 0.06)",
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    {/* </Box> */}
                  </Stack>
                </Box>

                {/* CONVERSATION NOTES */}
                <Box
                  sx={{
                    height: "90%",
                    p: {
                      xs: 1,
                      sm: 1,
                    },
                    overflowY: "auto",
                  }}
                >
                  {/* <Typography
                    sx={{
                      mb: 1,
                      color: "text.secondary",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Conversation Notes
                  </Typography> */}

                  <Box
                    sx={{
                      color: "text.secondary",
                      fontSize: 14,
                      lineHeight: 1.7,

                      "& p": {
                        mt: 0,
                        mb: 1,
                      },

                      "& p:last-child": {
                        mb: 0,
                      },
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          getDescription(entry),
                      }}
                    />
                  </Box>
                </Box>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default FollowUpHistoryView;

